import { useMemo, useState } from 'react';

import {
  Assets,
  Button,
  ButtonTextOrIcon,
  Feedback,
  Form,
  useDatePickerInput,
  UseDatePickerInputArgs,
  useSelectSingle,
} from '@foradevelopment/fora-ui';
import { EmploymentViewModelNew, UpdateEmploymentInput } from '@fora-monorepo/employer-my-pages-common/types';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { updateEmployment } from '../../redux/employment/employmentThunks';
import {
  addOneDay,
  getMarkupStartDateRangeMax,
  getMarkupStartDateRangeMin,
  trackTraceMarkupStartDateValidation,
} from '../utils';
import { envConfig } from '../../config';
import {
  validateEmploymentPeriodEndDate,
  validateEmploymentPeriodStartDate,
  validateMarkupStartDate,
} from '../../common/utils';
import { EmploymentFormStepWrapper, EmploymentPeriodForm, MarkupForm } from '../../components';
import { useTypedTranslation } from '../../i18n/i18n';

export const AddNewEmploymentPeriodForm = ({
  goToConfirmationPanel,
  employment,
}: {
  goToConfirmationPanel: (employment: EmploymentViewModelNew) => void;
  employment: EmploymentViewModelNew;
}) => {
  const { employeeId } = employment.employee;
  const { employmentPeriods } = employment;

  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const { faGroups, insuranceAgreementValidFromDate } = useAppSelector(state => state.employer.employerInfo);
  const monthlyReportingReleaseDate = useAppSelector(state => state.foraGlobal.settings.monthlyReportingReleaseDate);
  const { ffFaGroupIdOnEmployment } = useAppSelector(state => state.foraGlobal.settings);

  const [errorSubmittingMessage, setErrorSubmittingMessage] = useState('');
  const [submittingForm, setSubmittingForm] = useState(false);

  // EmploymentPeriodForm
  const startEmploymentInput = useDatePickerInput({});
  const endEmploymentInput = useDatePickerInput({});

  const latestEmploymentPeriod = employmentPeriods[employmentPeriods.length - 1];
  startEmploymentInput.props.minDate = latestEmploymentPeriod?.employmentEndDate
    ? addOneDay(latestEmploymentPeriod?.employmentEndDate)
    : '';
  startEmploymentInput.props.maxDate = endEmploymentInput.value ?? '';
  endEmploymentInput.props.minDate = startEmploymentInput.value ?? '';
  endEmploymentInput.props.disabled = !startEmploymentInput.value && !endEmploymentInput.value;

  // MarkupAndParametersForm
  const markupSelectInput = useSelectSingle();

  const markupDatePickerInputSettings = useMemo((): UseDatePickerInputArgs => {
    return {
      validate: validateMarkupStartDate(
        true,
        monthlyReportingReleaseDate,
        faGroups,
        employeeId,
        startEmploymentInput.value
      ),
      minDate: getMarkupStartDateRangeMin(
        monthlyReportingReleaseDate,
        insuranceAgreementValidFromDate,
        employeeId,
        startEmploymentInput.value
      ),
      maxDate: getMarkupStartDateRangeMax(employeeId, endEmploymentInput.value),
    };
  }, [
    employeeId,
    monthlyReportingReleaseDate,
    faGroups,
    startEmploymentInput.value,
    insuranceAgreementValidFromDate,
    endEmploymentInput.value,
  ]);

  const markupStartDateInput = useDatePickerInput(markupDatePickerInputSettings);

  // TODO, move this to parameter component ?
  // const [parameterChoicesInput, setParameterChoicesInput] = useState<
  //   { fokParameter: FokParameterChoiceInput; validateInput: () => boolean }[]
  // >([]);

  const submitAddEmploymentPeriod = async () => {
    setSubmittingForm(true);
    const errorMessages: string[] = [];

    const startValidationErrors = validateEmploymentPeriodStartDate(endEmploymentInput.value)(
      startEmploymentInput.value
    );
    errorMessages.push(...startValidationErrors);
    startEmploymentInput.props.errors = startValidationErrors;

    const endValidationErrors = validateEmploymentPeriodEndDate(startEmploymentInput.value)(endEmploymentInput.value);
    errorMessages.push(...endValidationErrors);
    endEmploymentInput.props.errors = endValidationErrors;

    const markupStartDateValidationErrors = markupStartDateInput.validateDateInputValue();
    if (markupStartDateValidationErrors.length > 0) {
      trackTraceMarkupStartDateValidation(markupStartDateInput.value, markupStartDateValidationErrors);
    }
    errorMessages.push(...markupStartDateValidationErrors);

    if (markupStartDateInput.value) {
      const markupSelectInvalid = !markupSelectInput.validate();
      if (markupSelectInvalid) errorMessages.push('markupSelectInvalid');
    }

    const formValid = errorMessages.length === 0;

    if (!formValid) {
      setSubmittingForm(false);
      return;
    }

    const updateEmploymentInput: UpdateEmploymentInput = {
      partnerNumberIndividual: employment.employee.partnerNumberIndividual,
      employmentPeriod: {
        index: employment.employmentPeriods.length,
        employmentEndDate: endEmploymentInput.value,
        employmentStartDate: startEmploymentInput.value,
        markups: [{ startDate: markupStartDateInput.value, fokId: markupSelectInput.props.value }],
        fokParameterChoices: [],
      },
    };

    setErrorSubmittingMessage('');

    const result = await dispatch(updateEmployment({ unchangedEmployment: employment, updateEmploymentInput }));
    if (updateEmployment.fulfilled.match(result)) {
      const updatedEmployment = result.payload;
      trackCustomEvent('Added new employment period');
      goToConfirmationPanel(updatedEmployment);
    }
    if (updateEmployment.rejected.match(result)) {
      setErrorSubmittingMessage(
        envConfig.devLogging && result.payload && result.payload.message
          ? `Fel i backend: ${result.payload.message}`
          : t('errorMessages.contactSupport')
      );
      setSubmittingForm(false);
    }
  };

  return (
    <Form preventDefault backgroundColor="transparent">
      <EmploymentFormStepWrapper
        heading={t('form.employmentPeriod.title')}
        ingress={t('form.employmentPeriod.description')}
        isFirstStep
      >
        <EmploymentPeriodForm
          startDateInputProps={startEmploymentInput.props}
          endDateInputProps={endEmploymentInput.props}
        />
      </EmploymentFormStepWrapper>
      <EmploymentFormStepWrapper
        heading={t('form.editEmployment.markupTitle')}
        ingress={t('form.markup.addMarkupDescription')}
        isFirstStep
      >
        <MarkupForm
          ffFaGroupIdOnEmployment={ffFaGroupIdOnEmployment}
          markupOptions={faGroups}
          startDateInputProps={markupStartDateInput.props}
          startDateInputValue={markupStartDateInput.value}
          markupSelectInput={markupSelectInput}
          formType="add"
        />
      </EmploymentFormStepWrapper>
      {errorSubmittingMessage && (
        <Feedback type="error" marginBottom={40} negativeSideMargins>
          {errorSubmittingMessage}
        </Feedback>
      )}
      <Button type="button" onClick={submitAddEmploymentPeriod} disabled={submittingForm} fullWidth={false}>
        <ButtonTextOrIcon
          icon={<Assets.Spinner color="white" width={20} center={false} />}
          textColor="white"
          isLoading={submittingForm}
        >
          {t('common.save')}
        </ButtonTextOrIcon>
      </Button>
    </Form>
  );
};
